<template>
  <div>
    <b-card title="Introduction">
      <b-card-body>
        <p>
          You should have seen truth tables in previous courses. However, we're going to formalize a few things in order to help us create circuits
          with the minimum number of gates.
        </p>

        <p>
          Truth tables are simply tables that define the output of a Boolean variable from Boolean variable inputs. From this table we can derive a
          Boolean expression, whose outcome is either true or false, in other words the outcome is Boolean itself.
       </p>

       <p>
          The headers of the truth table are Boolean variables divided into output and input respectively. The headers on the left are input, and 
          headers on the right (at least the last one), are output. In reality, a single truth table has exactly one output. But for many sets of 
          input variables we might define more than one output expression. Rather than right a new table for each input, we just combine them into 
          one table, with all the output variables on the right side.
       </p>
      </b-card-body>
    </b-card><p/>
    <b-card title="Creating a Truth Table">
      <b-card-body>
        Number of Input Variables: <b-form-input class="variables" v-model="inputs" type="number" max="3" min="1"/>
        Number of Output Variables: <b-form-input class="variables" v-model="outputs" type="number" max="3" min="1"/><p/>
        <b-table class="truth-table" :items="truthTable">
          <template #thead-top>
            <b-tr>
              <b-th :colspan="inputs" variant="secondary">Inputs</b-th>
              <b-th :colspan="outputs">Outputs</b-th>
            </b-tr>
          </template>
          <template #head(d)="data">
            <span id="dLabel">{{data.label.toLowerCase()}}</span>
          </template>
          <template #head(e)="data">
            <span id="eLabel">{{data.label.toLowerCase()}}</span>
          </template>
          <template #head(f)="data">
            <span id="fLabel">{{data.label.toLowerCase()}}</span>
          </template>
          <template #cell(d)="data">
            <triState v-model="dState[data.index]" :state="dState[data.index]"/>
          </template>
          <template #cell(e)="data">
            <triState v-model="eState[data.index]" :state="eState[data.index]"/>
          </template>
          <template #cell(f)="data">
            <triState v-model="fState[data.index]" :state="fState[data.index]"/>
          </template>
        </b-table>
      </b-card-body>
    </b-card><p/>
    <b-popover custom-class="expression" target="dLabel" triggers="hover" placement="topright" variant="success">
      d = {{dExpression}}
    </b-popover>
    <b-popover custom-class="expression" target="eLabel" triggers="hover" placement="topright" variant="success">
      e = {{eExpression}}
    </b-popover>
    <b-popover custom-class="expression" target="fLabel" triggers="hover" placement="topright" variant="success">
      f = {{fExpression}}
    </b-popover>
    <b-card title="Cardinality of Truth Tables">
      <b-card-body>
        <p>
          The cardinality of truth tables refers to the number of rows for a truth table, and the possible number of unique truth tables 
          for a given number of inputs. For example, if a truth table has 2 input variables then there are a total of 2<sup>2</sup> or 4 
          rows in that truth table. Since there are 4 rows, then the are 2<sup>4</sup> possible unique outputs. Another way to think of it
          is that the output column represents an 4-bit number. Therefore, there are 2<sup>4</sup> possible combinations of outputs, or 16. 
          We call each of these possible combinations a single possible truth table. Said another way, there are 16 different operations that
          can be defined by a truth table with 2 input variables. 
        </p>

        <p>
          More generally, for n input variables, there are 2<sup>n</sup> rows and therefore 2<sup>2<sup>n</sup></sup> different possible truth
          tables possible.
        </p>

        <p>
          <b>Try it yourself:</b>
        </p>

        <p class="example">
          Number of input variables (n) = <b-form-input class="variables" v-model="n" type="number" min="1" max="10"/>
        </p>

        <p class="example"> 
          For a truth table with {{n}} variable{{this.n > 1 ? 's' : ''}}, there are 2<sup>{{n}}</sup> = {{2 ** n}} rows and there are a total of 2<sup>2<sup>{{n}}</sup></sup> = {{(2 ** (2 ** n)).toLocaleString()}} possible 
          truth tables or operations that maybe defined for such a truth table.
        </p>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {fillBinary} from '@/util/numbers'
import triState from '@/components/tri-state'

export default {
  name: 'tt-intruduction',
  data: function () {
    return {
      inputs: 2,
      outputs: 1,
      n: 1,
      dState: [null, null, null, null, null, null, null, null],
      dExpression: '',
      eState: [null, null, null, null, null, null, null, null],
      eExpression: '',
      fState: [null, null, null, null, null, null, null, null],
      fExpression: '',
      variableNames: ['a', 'b', 'c', 'd', 'e', 'f']
    }
  },
  computed: {
    truthTable: function () {
      var table = []
      for (var i = 0; i < (2 ** this.inputs); i++) {
        var bits = fillBinary(i.toString(2), this.inputs)
        var row = {}
        for (var j = 0; j < this.inputs; j++) {
          row[this.variableNames[j]] = bits.charAt(j) - '0'
        }
        for (var k = 0; k < this.outputs; k++) {
          row[this.variableNames[k + 3]] = 0
        }
        table.push(row)
      }
      return table
    }
  },
  components: {
    triState
  },
  methods: {
    isComplete: function (states) {
      var complete = true
      for (var i = 0; i < 2 ** this.inputs && complete; i++) {
        complete = complete && states[i] != null
      }
      return complete
    },
    computeExpression: function (states) {
      var expression = ''
      for (var i = 0; i < 2 ** this.inputs; i++) {
        if (states[i] === 1) {
          var raw = fillBinary(i.toString(2), this.inputs)
          var term = ''
          for (var j = 0; j < this.inputs; j++) {
            term += this.variableNames[j] + (raw[j] === '0' ? '\'' : '')
          }
          expression += ' + ' + term
        }
      }
      return expression.substring(3)
    }
  },
  watch: {
    dState: function (oldState, newState) {
      var complete = this.isComplete(this.dState)
      if (complete) {
        this.dExpression = this.computeExpression(this.dState)
      }
    },
    eState: function (oldState, newState) {
      var complete = this.isComplete(this.eState)
      if (complete) {
        this.eExpression = this.computeExpression(this.eState)
      }
    },
    fState: function (oldState, newState) {
      var complete = this.isComplete(this.fState)
      if (complete) {
        this.fExpression = this.computeExpression(this.fState)
      }
    }
  }
}
</script>
<style scoped>
div {
  text-align: left;
}

.variables {
  max-width: 75px;
  display: inline-block;
}

.truth-table {
  max-width: 300px;
}

.example, .expression, .tooltip, .tootltip-inner {
  font-size: x-large;
  display: inline-block;
  width: fit-content;
  max-width: none;
}

</style>



// WEBPACK FOOTER //
// src/components/tt-introduction.vue